import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../../components/ui/dialog';
import { Plus, Trash2, Edit2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CategoriesSection = ({ categories, language, onRefresh, onDelete }) => (
  <div className="space-y-6">
    <div className="bg-white border border-stone-200 p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-stone-900 font-semibold">{language === 'sv' ? 'Huvudkategorier' : 'Main Categories'}</h3>
        <CategoryFormDialog onSuccess={onRefresh} language={language} />
      </div>
      {categories.map(mainCat => (
        <div key={mainCat.id} className="mb-6 border-b border-stone-100 pb-4 last:border-0">
          <div className="flex items-center justify-between bg-stone-50 p-3 mb-2">
            <span className="font-medium text-stone-900">{language === 'sv' ? mainCat.name_sv : mainCat.name_en}</span>
            <div className="flex gap-1">
              <CategoryFormDialog onSuccess={onRefresh} language={language} parentId={mainCat.id} />
              <CategoryFormDialog onSuccess={onRefresh} language={language} editData={mainCat} />
              <Button variant="ghost" size="icon" onClick={() => onDelete(mainCat.id)} className="text-stone-500 hover:text-red-600">
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
          {mainCat.children?.length > 0 && (
            <div className="ml-6 space-y-2">
              {mainCat.children.map(subCat => (
                <div key={subCat.id} className="flex items-center justify-between p-2 bg-white border border-stone-100">
                  <span className="text-stone-700">↳ {language === 'sv' ? subCat.name_sv : subCat.name_en}</span>
                  <div className="flex gap-1">
                    <CategoryFormDialog onSuccess={onRefresh} language={language} editData={subCat} />
                    <Button variant="ghost" size="icon" onClick={() => onDelete(subCat.id)} className="text-stone-500 hover:text-red-600">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  </div>
);

const CategoryFormDialog = ({ onSuccess, language, editData = null, parentId = null }) => {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [name_sv, setNameSv] = useState('');
  const [name_en, setNameEn] = useState('');
  const [description_sv, setDescriptionSv] = useState('');
  const [description_en, setDescriptionEn] = useState('');
  const [slug, setSlug] = useState('');
  const [order, setOrder] = useState(0);

  useEffect(() => {
    if (editData && open) {
      setNameSv(editData.name_sv || '');
      setNameEn(editData.name_en || '');
      setDescriptionSv(editData.description_sv || '');
      setDescriptionEn(editData.description_en || '');
      setSlug(editData.slug || '');
      setOrder(editData.order || 0);
    } else if (open) {
      setNameSv(''); 
      setNameEn(''); 
      setDescriptionSv('');
      setDescriptionEn('');
      setSlug(''); 
      setOrder(0);
    }
  }, [editData, open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const token = localStorage.getItem('admin_token');
      const data = { 
        name_sv, 
        name_en, 
        description_sv,
        description_en,
        slug: slug || null,  // Let backend generate from name_sv
        order: parseInt(order) || 0 
      };
      
      if (parentId) data.parent_id = parentId;
      if (editData) data.parent_id = editData.parent_id || null;
      
      if (editData) {
        await axios.put(`${API}/categories/${editData.id}`, data, {
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
        });
        toast.success(language === 'sv' ? 'Kategori uppdaterad' : 'Category updated');
      } else {
        await axios.post(`${API}/categories`, data, {
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
        });
        toast.success(language === 'sv' ? 'Kategori skapad' : 'Category created');
      }
      setOpen(false);
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    } finally {
      setSubmitting(false);
    }
  };

  const getButtonLabel = () => {
    if (editData) return <Edit2 className="w-4 h-4" />;
    if (parentId) return <><Plus className="w-4 h-4" /> {language === 'sv' ? 'Underkategori' : 'Subcategory'}</>;
    return <><Plus className="w-4 h-4 mr-2" /> {language === 'sv' ? 'Ny huvudkategori' : 'New main category'}</>;
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {editData ? (
          <Button variant="ghost" size="icon" className="text-stone-500 hover:text-stone-700"><Edit2 className="w-4 h-4" /></Button>
        ) : parentId ? (
          <Button variant="outline" size="sm" className="text-xs border-stone-300">{getButtonLabel()}</Button>
        ) : (
          <Button className="bg-stone-900 text-white hover:bg-stone-800">{getButtonLabel()}</Button>
        )}
      </DialogTrigger>
      <DialogContent className="bg-white border-stone-200">
        <DialogHeader>
          <DialogTitle>{editData ? (language === 'sv' ? 'Redigera kategori' : 'Edit category') : parentId ? (language === 'sv' ? 'Ny underkategori' : 'New subcategory') : (language === 'sv' ? 'Ny huvudkategori' : 'New main category')}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Namn (SV)</Label><Input value={name_sv} onChange={(e) => setNameSv(e.target.value)} required className="bg-white border-stone-300" /></div>
            <div><Label>Name (EN)</Label><Input value={name_en} onChange={(e) => setNameEn(e.target.value)} required className="bg-white border-stone-300" /></div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Slug</Label><Input value={slug} onChange={(e) => setSlug(e.target.value)} placeholder="auto-generated" className="bg-white border-stone-300" /></div>
            <div><Label>Ordning</Label><Input type="number" value={order} onChange={(e) => setOrder(parseInt(e.target.value) || 0)} className="bg-white border-stone-300" /></div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Beskrivning (SV) - SEO-text</Label>
              <Textarea 
                value={description_sv}
                onChange={(e) => setDescriptionSv(e.target.value)}
                placeholder="SEO-text som visas på kategorisidan..."
                rows={4}
                className="bg-white border-stone-300"
              />
            </div>
            <div>
              <Label>Description (EN) - SEO text</Label>
              <Textarea 
                value={description_en}
                onChange={(e) => setDescriptionEn(e.target.value)}
                placeholder="SEO text shown on category page..."
                rows={4}
                className="bg-white border-stone-300"
              />
            </div>
          </div>
          <DialogFooter><Button type="submit" disabled={submitting} className="bg-stone-900 text-white">{submitting ? '...' : (language === 'sv' ? 'Spara' : 'Save')}</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CategoriesSection;
export { CategoryFormDialog };
